import _ from 'lodash';
import { GRANULARITY, DATE_RANGE_FORMAT, METRICS_METADATA } from './consts';
import { PM_PROVIDERS, SHORT_DATE_FORMAT } from 'common/constants/constants';
import BoldedValue from 'components/BoldedValue';
import TooltipWrapper from 'components/Tooltips/TooltipWrapper';
import Text from 'components/base/Text';
import {
  Flex,
  Margin,
  Padding,
  LegendSymbol
} from 'components/base/layout-components';
import { SPACES } from 'common/constants/sizes';
import moment from 'moment';
import { renderToString } from 'react-dom/server';
import { isMulti, calculatePercentage, pluralize } from 'common/utils/utils';
import { metricNames } from '@linearb/linjs-common';
import Icon from 'components/base/Icon';
import {
  MultiSeriesLegend,
  SeriesData,
  SeriesIcon,
  SeriesName
} from './Tooltip.style';
import ComputedMetricTooltip from './ComputedMetricTooltip';
import ClickableMessage from './TooltipClickableMessage';

const tooltipLegend = ({ data = [] }) => {
  return (
    <Padding padding='x-x-small' b='no-space'>
      {data.map(({ title, value, color }, idx) => {
        return (
          <Flex key={idx} items='center' pb='x-small' data-testid='legend-item'>
            <Margin r='x-small'>
              {<LegendSymbol backgroundColor={color} />}
            </Margin>
            <Text size='small' mr='0.5rem' align='end'>
              {value}
            </Text>
            <Text>{_.capitalize(title)}</Text>
          </Flex>
        );
      })}
    </Padding>
  );
};

export const Tooltip = ({
  title,
  data,
  label,
  text,
  percent = null,
  percentDescription,
  legend,
  isClickable,
  isAreaSelectEnabled,
  isPmCustomDashboard,
  isReportedMetric,
  widgetFilters,
  shouldHideClickableMessage,
  pmProvider
}) => {
  return (
    <TooltipWrapper title={title}>
      <Flex direction='column' padding='small'>
        {text && (
          <Text size='medium' lineHeight={SPACES.medium}>
            {text}
          </Text>
        )}
        {(data || label) && (
          <BoldedValue
            dataCy='tooltip-value-cy'
            data-testid='data-label'
            value={{ text: data, size: 'x-large' }}
            description={{
              text: isReportedMetric ? widgetFilters?.unit : label,
              size: 'small'
            }}
          />
        )}
        {!_.isNull(percent) ? (
          <Flex pb='small' items='center' data-testid='percent-description'>
            <Text size='x-x-large' pr={SPACES['x-small']}>
              {percent}%
            </Text>
            {percentDescription && <Text>{percentDescription}</Text>}
          </Flex>
        ) : null}
        {legend && tooltipLegend({ data: legend })}
        {!shouldHideClickableMessage && (
          <ClickableMessage
            isAreaSelectEnabled={isAreaSelectEnabled}
            isClickable={isClickable}
          />
        )}
        {isPmCustomDashboard && pmProvider === PM_PROVIDERS.JIRA && (
          <Flex mt='small' items='center'>
            <Text mr='0.3rem' color='shuttleGray' size='medium'>
              Click to view in Jira
            </Text>
            <Icon name='linkArrow' color='shuttleGray' size='small' />
          </Flex>
        )}
      </Flex>
    </TooltipWrapper>
  );
};

export const MultiSeriesTooltip = ({ title, isClickable, points }) => {
  const orderedPoints = _.orderBy(points, ['y'], ['desc']);
  return (
    <TooltipWrapper title={title}>
      <Flex direction='column' padding='small'>
        {_.map(orderedPoints, point => {
          const { id, color, data } = point;
          const name = _.get(point, 'series.name');
          return (
            <MultiSeriesLegend
              key={id}
              $color={color}
              $selected={point.selected}
              data-cy='MultiSeriesLegend'
            >
              <SeriesIcon $color={color} />
              <SeriesName>{name}</SeriesName>
              <SeriesData>{data}</SeriesData>
            </MultiSeriesLegend>
          );
        })}
      </Flex>
    </TooltipWrapper>
  );
};

export const CFRTooltip = ({
  title,
  data,
  percent,
  isAreaSelectEnabled,
  isClickable,
  shouldHideClickableMessage
}) => {
  return (
    <TooltipWrapper title={title}>
      <Flex direction='column' padding='small'>
        <Text data-cy='tooltip-value-cy'>
          {percent || 0} Deployments | {data || 0} Incidents
        </Text>
        {!shouldHideClickableMessage && (
          <ClickableMessage
            isAreaSelectEnabled={isAreaSelectEnabled}
            isClickable={isClickable}
          />
        )}
      </Flex>
    </TooltipWrapper>
  );
};

export const toolTipFormatter = ({
  title,
  data,
  text,
  label,
  percent,
  percentDescription,
  legend,
  metricName = '',
  isClickable = false,
  isAreaSelectEnabled,
  shouldHideClickableMessage = true,
  points = [],
  rawData = null,
  currentPercentage = null,
  computedMetrics = null,
  isPmCustomDashboard = false,
  isReportedMetric = false,
  widgetFilters,
  pmProvider = PM_PROVIDERS.JIRA
}) => {
  const isComputedMetric = !!computedMetrics;
  return renderToString(
    isMulti(points) ? (
      <MultiSeriesTooltip
        title={title}
        isClickable={isClickable}
        points={points}
      />
    ) : isComputedMetric ? (
      <ComputedMetricTooltip
        title={title}
        data={data}
        computedMetrics={computedMetrics}
        isClickable={isClickable}
        isAreaSelectEnabled={isAreaSelectEnabled}
        shouldHideClickableMessage={shouldHideClickableMessage}
      />
    ) : metricName === metricNames.PM_CFR ? (
      <CFRTooltip
        title={title}
        data={rawData}
        percent={currentPercentage}
        isClickable={isClickable}
        isAreaSelectEnabled={isAreaSelectEnabled}
        shouldHideClickableMessage={shouldHideClickableMessage}
      />
    ) : (
      <Tooltip
        title={title}
        data={data}
        text={text}
        label={label}
        percent={percent}
        percentDescription={percentDescription}
        legend={legend}
        isClickable={isClickable}
        isAreaSelectEnabled={isAreaSelectEnabled}
        isPmCustomDashboard={isPmCustomDashboard}
        isReportedMetric={isReportedMetric}
        widgetFilters={widgetFilters}
        shouldHideClickableMessage={shouldHideClickableMessage}
        pmProvider={pmProvider}
      />
    )
  );
};

export const getToolTipTitleByDateRange = (
  after,
  before,
  granularity = GRANULARITY.DAYS,
  name = 'Iteration'
) => {
  const granularityMap = {
    [GRANULARITY.DAYS]: moment(before).format('dddd, MMM D'),
    [GRANULARITY.WEEKS]: `Week ${moment(after).isoWeek()} (${moment(
      after
    ).format(DATE_RANGE_FORMAT)} - ${moment(before).format(
      DATE_RANGE_FORMAT
    )})`,
    [GRANULARITY.MONTHS]: `${moment(before).format('MMMM')} (${moment(
      after
    ).format(DATE_RANGE_FORMAT)} - ${moment(before).format(
      DATE_RANGE_FORMAT
    )})`,
    [GRANULARITY.ITERATIONS]: `${name} (${moment(after).format(
      DATE_RANGE_FORMAT
    )} - ${moment(before).format(DATE_RANGE_FORMAT)})`
  };

  return granularityMap[granularity];
};

export const getTooltipLegendData = (point, series, metGoals) => {
  const legend = [];

  const thisIndex = point.index;
  const allSeries = series.chart.series;
  allSeries.forEach(ser => {
    if (ser.options.stacking === point.series.options.stacking) {
      legend.push({
        title: ser.name,
        color: ser.color,
        value: ser.points[thisIndex].y
      });
    }
  });

  const pointSum = _.sum(legend.map(({ value }) => value)) || 0;
  const goalValue = _.get(
    _.find(legend, ({ title }) => title === metGoals),
    'value',
    0
  );
  const percent = calculatePercentage(goalValue, pointSum);

  return { legend, percent };
};

export const getGoalsSummaryTooltipTitle = ({
  metrics,
  metricName,
  dateRange = {},
  didNotMeetGoalTooltipDescription
}) => {
  const { start_date = moment(), end_date = moment() } = dateRange;
  const dateFormattted = `${moment(start_date).format(
    SHORT_DATE_FORMAT
  )} - ${moment(end_date).format(SHORT_DATE_FORMAT)}`;
  const metricsData = _.get(metrics, '[0].data', []);
  const prMergedSum = _.sum(
    metricsData.map(item => item['pr.merged']).filter(Boolean)
  );
  const didNotMeetGoalSum = _.sum(
    metricsData.map(item => item[metricName]).filter(Boolean)
  );

  return (
    <Flex direction='column' padding='small'>
      <Text size='medium' weight='500' pb={SPACES.small}>
        {dateFormattted}
      </Text>
      <Text
        size='medium'
        color='shuttleGray'
        pb={SPACES.small}
        data-cy='goals-summary-tooltip-text'
      >
        <b>{prMergedSum}</b> PRs merged
      </Text>
      <Text size='medium' color='shuttleGray'>
        <b>{didNotMeetGoalSum}</b> {didNotMeetGoalTooltipDescription}
      </Text>
    </Flex>
  );
};

export const getAnnotationTooltipText = ({
  graphId,
  granularity = GRANULARITY.DAYS,
  created,
  threshholds = {},
  changesPerPreiod = 0
} = {}) => {
  const graphData = METRICS_METADATA[graphId];
  if (!graphData) return '';

  const { annotationTooltip } = graphData;

  const dateFormattted =
    granularity !== GRANULARITY.DAYS
      ? `${moment(created).format(SHORT_DATE_FORMAT)}: `
      : '';

  const changes = pluralize(changesPerPreiod - 1, 'change', 's', false);

  const changesPerPeriodMessage =
    changesPerPreiod > 1
      ? ` (plus ${changesPerPreiod - 1} older ${changes})`
      : '';

  return `${dateFormattted}${annotationTooltip(
    threshholds
  )}${changesPerPeriodMessage}`;
};
